/**
 * Binds the synchronous tenant cache (devStorage) to the MongoDB backend.
 *
 * Domain data lives in dedicated backend collections:
 *   - finance_records_v1     -> /api/finance
 *   - hand_loan_records_v1   -> /api/handloans
 *   - admin_emi_payments_v1  -> /api/emi-payments
 *   - consultancy_bundle_v1  -> /api/consultancy
 * These are written explicitly by workspaceStore / consultancyStore, so the
 * generic key/value mirror below SKIPS them to avoid double writes.
 *
 * Everything else (settings: owners, lines, payments, workspace admins, profile)
 * is mirrored generically to /api/workspace/kv/:key.
 *
 * On login/dashboard mount, `hydrateTenantWorkspace()` pulls every domain + the
 * settings KV into the local cache. Sync only runs when a backend JWT is present.
 */
import {
  workspaceApi,
  financeApi,
  handLoanApi,
  emiApi,
  consultancyApi,
  accountingApi,
  hasAuthToken,
} from '../api';
import {
  registerCloudPersistence,
  tenantScopedKey,
  listTenantRawKeys,
  loadDevJson,
} from './devStorage';
import { hydrateCompanyProfile, COMPANY_PROFILE_KEY } from './companyProfile';
import { hydrateFinancialYear } from './financialYear';
import { handleSyncRejection } from './periodLockNotice';

const WORKSPACE_DATA_EVENT = 'workspace-data';
const CONSULTANCY_DATA_EVENT = 'consultancy-data';
const ACCOUNTING_DATA_EVENT = 'accounting-data';

const FIN_KEY = 'finance_records_v1';
const HAND_KEY = 'hand_loan_records_v1';
const EMI_KEY = 'admin_emi_payments_v1';
const BUNDLE_KEY = 'consultancy_bundle_v1';
const ACCT_KEY = 'accounting_bundle_v1';

/** Keys handled by dedicated endpoints (excluded from the generic KV mirror). */
const DEDICATED_KEYS = new Set([FIN_KEY, HAND_KEY, EMI_KEY, BUNDLE_KEY, ACCT_KEY, COMPANY_PROFILE_KEY]);

const PHOTO_KEYS = ['applicantPhoto', 'avatar', 'photo', 'customerPhoto', 'photoUrl'] as const;

function loadablePhoto(data: Record<string, unknown> | undefined): string {
  if (!data) return '';
  for (const key of PHOTO_KEYS) {
    const value = String(data[key] ?? '').trim();
    if (/^(https?:|data:|blob:)/i.test(value)) return value;
  }
  return '';
}

/** A server refresh must not replace a photo the browser can already show. */
function mergePhotoRecords<T extends { id: string; data?: Record<string, unknown> }>(local: T[], server: T[]): T[] {
  const byId = new Map(local.map((row) => [row.id, row]));
  return server.map((row) => {
    const localPhoto = loadablePhoto(byId.get(row.id)?.data);
    if (localPhoto && !loadablePhoto(row.data)) {
      return { ...row, data: { ...(row.data || {}), applicantPhoto: localPhoto } };
    }
    return row;
  });
}

function emitWorkspace() {
  try {
    window.dispatchEvent(new Event(WORKSPACE_DATA_EVENT));
  } catch {
    /* no window */
  }
}
function emitConsultancy() {
  try {
    window.dispatchEvent(new Event(CONSULTANCY_DATA_EVENT));
  } catch {
    /* no window */
  }
}
function emitAccounting() {
  try {
    window.dispatchEvent(new Event(ACCOUNTING_DATA_EVENT));
  } catch {
    /* no window */
  }
}

/** Write a value straight into the tenant cache without re-triggering a cloud write. */
function cacheSet(rawKey: string, value: unknown) {
  try {
    localStorage.setItem(tenantScopedKey(rawKey), JSON.stringify(value));
  } catch {
    /* quota */
  }
}

// ---- Generic KV mirror (settings only) ----

const pending = new Map<string, ReturnType<typeof setTimeout>>();
const DEBOUNCE_MS = 400;

function schedulePut(rawKey: string, value: unknown) {
  if (!hasAuthToken() || DEDICATED_KEYS.has(rawKey)) return;
  const existing = pending.get(rawKey);
  if (existing) clearTimeout(existing);
  pending.set(
    rawKey,
    setTimeout(() => {
      pending.delete(rawKey);
      workspaceApi.putKey(rawKey, value).catch((e) => {
        handleSyncRejection(e);
      });
    }, DEBOUNCE_MS)
  );
}

function scheduleRemove(rawKey: string) {
  if (!hasAuthToken() || DEDICATED_KEYS.has(rawKey)) return;
  workspaceApi.deleteKey(rawKey).catch(() => {});
}

registerCloudPersistence({ put: schedulePut, remove: scheduleRemove });

// ---- Hydration ----

let hydrating = false;

/**
 * Pull the server's full workspace state into the local cache for the active
 * tenant. Returns true when sync ran (backend session + reachable).
 */
export async function hydrateTenantWorkspace(): Promise<boolean> {
  if (!hasAuthToken() || hydrating) return false;
  hydrating = true;
  try {
    const [finance, handloans, emi, consultancy, accounting, kv] = await Promise.allSettled([
      financeApi.list(),
      handLoanApi.list(),
      emiApi.list(),
      consultancyApi.get(),
      accountingApi.get(),
      workspaceApi.getState(),
    ]);

    // Company identity (branding) — pull into the synchronous cache for PDFs.
    hydrateCompanyProfile().catch(() => {});
    hydrateFinancialYear().catch(() => {});

    let serverHasDomainData = false;

    if (finance.status === 'fulfilled') {
      const local = loadDevJson<Array<{ id: string; data?: Record<string, unknown> }>>(FIN_KEY, []);
      cacheSet(FIN_KEY, mergePhotoRecords(local, finance.value.records));
      if (finance.value.records.length) serverHasDomainData = true;
    }
    if (handloans.status === 'fulfilled') {
      const local = loadDevJson<Array<{ id: string; data?: Record<string, unknown> }>>(HAND_KEY, []);
      cacheSet(HAND_KEY, mergePhotoRecords(local, handloans.value.records));
      if (handloans.value.records.length) serverHasDomainData = true;
    }
    if (emi.status === 'fulfilled') {
      cacheSet(EMI_KEY, emi.value.payments);
      if (emi.value.payments.length) serverHasDomainData = true;
    }
    if (consultancy.status === 'fulfilled') {
      cacheSet(BUNDLE_KEY, consultancy.value.bundle);
    }
    if (accounting.status === 'fulfilled') {
      cacheSet(ACCT_KEY, accounting.value.bundle);
    }
    if (kv.status === 'fulfilled') {
      for (const item of kv.value.items) {
        if (!DEDICATED_KEYS.has(item.key)) cacheSet(item.key, item.value);
      }
    }

    emitWorkspace();
    emitConsultancy();
    emitAccounting();

    // First sync for this tenant: if the server has no domain data yet but the
    // browser does, push local data up so nothing is lost.
    if (!serverHasDomainData) {
      await migrateLocalToCloud();
    }
    return true;
  } catch {
    return false;
  } finally {
    hydrating = false;
  }
}

/** Push locally cached domain + settings data to the backend (best effort). */
export async function migrateLocalToCloud(): Promise<void> {
  if (!hasAuthToken()) return;

  const finance = loadDevJson<Array<{ id: string; source?: string; data: Record<string, unknown> }>>(FIN_KEY, []);
  const hands = loadDevJson<Array<{ id: string; source?: string; data: Record<string, unknown> }>>(HAND_KEY, []);
  const emi = loadDevJson<Array<Record<string, unknown>>>(EMI_KEY, []);
  const bundle = loadDevJson<Record<string, unknown> | null>(BUNDLE_KEY, null);
  const acctBundle = loadDevJson<Record<string, unknown> | null>(ACCT_KEY, null);

  const jobs: Promise<unknown>[] = [];
  for (const r of finance) {
    if (r && r.id) jobs.push(financeApi.upsert({ id: r.id, source: r.source, data: r.data }));
  }
  for (const r of hands) {
    if (r && r.id) jobs.push(handLoanApi.upsert({ id: r.id, source: r.source, data: r.data }));
  }
  for (const p of emi) {
    if (p && p.id) jobs.push(emiApi.create(p));
  }
  if (bundle) jobs.push(consultancyApi.put(bundle));
  if (acctBundle) jobs.push(accountingApi.put(acctBundle));

  // Settings KV (skip dedicated keys).
  const settingsItems = listTenantRawKeys()
    .filter((k) => !DEDICATED_KEYS.has(k))
    .map((k) => ({ key: k, value: loadDevJson<unknown>(k, null) }));
  if (settingsItems.length) jobs.push(workspaceApi.bulkPut(settingsItems));

  await Promise.allSettled(jobs);
}
